import { useUploadContext } from '@components/Files/UploadProvider';
import type { TFileWithMeta } from '@components/Files/UploadProvider/types/TFileWithMeta';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type GetManyForUserResponseDto, JobsService, MediaService, PayService } from 'app/API';
import { useMediaAnalysisPolling } from 'app/hooks/useMediaAnalysisPolling';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchMemberBillingAccountQueryFn } from 'app/queries/billing/createFetchMemberBillingAccountQueryFn';
import { createFetchHasPaymentMethodQueryFn } from 'app/queries/billing/createFetchHasPaymentMethodQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { createFetchAvailableLayoutsQueryFn } from 'app/queries/layouts/createFetchAvailableLayoutsQueryFn';
import { layoutsKeys } from 'app/queries/layouts/layoutsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { v4 } from 'uuid';

const BILLING_ACCOUNT_LOAD_ERROR = 'Unable to load your billing account. Close this window and try again.';
const ANALYSIS_SETUP_FAILED_MESSAGE = "We couldn't start analyzing this file. Remove it and upload it again.";

export enum BulkJobStep {
  SelectFiles = 'SelectFiles',
  UploadFiles = 'UploadFiles',
  AnalyzingFiles = 'AnalyzingFiles',
  CreateJobs = 'CreateJobs',
  SubmitPrice = 'SubmitPrice',
  JobsSubmittedSuccessfully = 'JobsSubmittedSuccessfully',
  JobsSubmittedWithErrors = 'JobsSubmittedWithErrors',
}

type TCreateJob = {
  id: string;
  layoutId?: string | null;
  name: string;
};

type TState = {
  step: BulkJobStep;
  setStep: (v: BulkJobStep) => void;
  isLoading: boolean;
  billingChecksInProgress: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  error: string | null;
  layouts: GetManyForUserResponseDto[];
  createJobs: (jobs: TCreateJob[]) => void;
  uploadFiles: () => void;
  jobs: TCreateJob[];
  totalDuration: number;
  submitJobs: () => void;
  isFormDisabled: boolean;
  balanceInCents: number;
  tierRate: number;
  refreshBalance: () => Promise<void>;
};

const ModalContext = createContext<TState>({
  step: BulkJobStep.SelectFiles,
  setStep: () => {},
  isLoading: false,
  billingChecksInProgress: true,
  onCancel: () => {},
  onSuccess: () => {},
  error: null,
  layouts: [],
  createJobs: () => {},
  uploadFiles: () => {},
  jobs: [],
  totalDuration: 0,
  submitJobs: () => {},
  isFormDisabled: false,
  balanceInCents: 0,
  tierRate: 0,
  refreshBalance: async () => {},
});

export type TModalProviderProps = {
  children: ReactNode;
  onCancel: () => void;
  onSuccess: () => void;
};

export const ModalProvider = ({ children, onCancel, onSuccess }: TModalProviderProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal } = useModalContext();
  const { workspace, organization } = useAuthContext();
  const { files, startAll, setExtra } = useUploadContext();
  const [step, setStep] = useState(BulkJobStep.SelectFiles);
  const [isProcessingUploads, setIsProcessingUploads] = useState(false);
  const [isFormDisabled, setFormDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<TCreateJob[]>([]);
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const payErrorMessageRef = useRef<string | null>(null);

  const baId = organization?.billingAccount?.id ?? '';
  const organizationId = organization?.id ?? '';

  const hasPaymentMethodQuery = useQuery({
    queryKey: billingKeys.hasPaymentMethodByAccount(baId),
    queryFn: createFetchHasPaymentMethodQueryFn(call),
    enabled: baId !== '',
    refetchOnMount: 'always',
  });
  const billingAccountQuery = useQuery({
    queryKey: billingKeys.memberAccount(baId),
    queryFn: createFetchMemberBillingAccountQueryFn(call),
    enabled: baId !== '',
  });
  const layoutsQuery = useQuery({
    queryKey: layoutsKeys.availableByOrganization(organizationId),
    queryFn: createFetchAvailableLayoutsQueryFn(call),
    enabled: !!workspace?.id && organizationId !== '',
  });
  useNotifyQueryError(hasPaymentMethodQuery.error);
  useNotifyQueryError(billingAccountQuery.error);
  useNotifyQueryError(layoutsQuery.error);

  const layouts = layoutsQuery.data ?? [];
  const balanceInCents = billingAccountQuery.data?.currentBalance ?? 0;
  const tierRate = Number(billingAccountQuery.data?.tierRate) || 0;
  const hasFreshPaymentMethodCheck = hasPaymentMethodQuery.isSuccess && hasPaymentMethodQuery.isFetchedAfterMount;
  const hasBillingAccount = billingAccountQuery.data !== undefined;
  const billingAccountLoadFailed = !hasBillingAccount && billingAccountQuery.isError;
  const billingChecksInProgress = !hasFreshPaymentMethodCheck || (!hasBillingAccount && !billingAccountLoadFailed);

  const refreshBalance = async () => {
    if (!baId) {
      return;
    }
    await billingAccountQuery.refetch();
  };

  const hasPaymentMethodResponse = hasFreshPaymentMethodCheck ? hasPaymentMethodQuery.data : undefined;
  const canViewBilling = !!organization?.permissions.viewBilling;
  const paymentMethodPromptShownRef = useRef(false);

  useEffect(() => {
    if (!hasPaymentMethodResponse || hasPaymentMethodResponse.result || paymentMethodPromptShownRef.current) {
      return;
    }
    paymentMethodPromptShownRef.current = true;
    if (!canViewBilling) {
      openModal(ModalNamesEnum.GenericErrorModal, {
        message: 'Your organization has no payment methods. Please contact administrator.',
      });
      return;
    }
    openModal(ModalNamesEnum.AddPaymentMethod, {
      onCancel,
      onSuccess: () => {
        openModal(ModalNamesEnum.BulkJobCreation, {
          onSuccess,
        });
      },
    });
  }, [hasPaymentMethodResponse, canViewBilling, openModal, onCancel, onSuccess]);

  const createDraftJobsMutation = useMutation({
    mutationFn: (uploadedFiles: TFileWithMeta[]) =>
      Promise.all(
        uploadedFiles.map(async (f) => {
          try {
            await call(
              () =>
                JobsService.createJob({
                  wsid: String(workspace?.id),
                  requestBody: {
                    id: f.meta.extra?.jobId,
                    name: 'Unnamed',
                    notes: '',
                    status: 'JOB_STATUS_DRAFT',
                  },
                }),
              { dontRedirectOnError: true },
            );
            const media = await call(
              () =>
                MediaService.createMedia({
                  requestBody: {
                    id: f.meta.id,
                    jobId: f.meta.extra?.jobId,
                    fileName: f.meta.name,
                    fileSize: f.file?.size?.toString() || '0',
                    weight: 0,
                  },
                }),
              { dontRedirectOnError: true },
            );
            setExtra(f.meta.id, { mediaId: media.id, analysisStatus: 'progress' });
          } catch {
            setExtra(f.meta.id, { analysisStatus: 'error', analysisErrorMessage: ANALYSIS_SETUP_FAILED_MESSAGE });
          }
        }),
      ),
    onError: notifyApiCallError,
  });
  const { mutate: createDraftJobs } = createDraftJobsMutation;

  useMediaAnalysisPolling({ files, setExtra });

  useEffect(() => {
    if (step !== BulkJobStep.SelectFiles) {
      return;
    }
    const erroredFile = files.find((f) => f.meta.status !== 'queued');
    setFormDisabled(!!erroredFile);
    files.map((f) => {
      if (!f.meta.extra?.jobId) {
        const jobId = v4();
        setExtra(f.meta.id, { jobId: jobId });
      }
    });
  }, [files, step, setExtra]);

  useEffect(() => {
    if (step !== BulkJobStep.UploadFiles) {
      return;
    }
    const notReadyToSubmitFile = files.find((f) => f.meta.status !== 'done');
    if (!notReadyToSubmitFile) {
      setStep(BulkJobStep.AnalyzingFiles);
      createDraftJobs(files);
    }
  }, [files, step, createDraftJobs]);

  useEffect(() => {
    if (step !== BulkJobStep.AnalyzingFiles) {
      return;
    }
    if (files.find((f) => f.meta.status !== 'done')) {
      return;
    }
    const notReadyToSubmitFile = files.find((f) => f.meta.extra?.analysisStatus !== 'done');
    if (!notReadyToSubmitFile) {
      const totalDuration = files
        .map((a) => a.meta.extra?.duration || 0)
        .reduce((acc: number, curr: number) => acc + curr, 0);
      setTotalDuration(totalDuration);
      setIsProcessingUploads(false);
    }
  }, [files, step]);

  const updateJobsMutation = useMutation({
    mutationFn: ({ workspaceId, jobsToUpdate }: { workspaceId: string; jobsToUpdate: TCreateJob[] }) =>
      call(() => JobsService.bulkUpdateJobs({ wsid: workspaceId, requestBody: { jobs: jobsToUpdate } })),
    onMutate: ({ jobsToUpdate }) => setJobs(jobsToUpdate),
    onSuccess: (data) => {
      if (!data.error) {
        setStep(BulkJobStep.SubmitPrice);
      } else {
        setError(data.error);
      }
    },
    onError: notifyApiCallError,
  });

  const createJobs = (jobsToUpdate: TCreateJob[]) => {
    if (!workspace?.id) {
      return;
    }
    updateJobsMutation.mutate({ workspaceId: workspace.id, jobsToUpdate });
  };

  const uploadFiles = () => {
    if (!workspace?.id) {
      return;
    }
    setIsProcessingUploads(true);
    setStep(BulkJobStep.UploadFiles);
    startAll();
  };

  const submitJobsMutation = useMutation({
    mutationFn: () => {
      payErrorMessageRef.current = null;
      return call(
        () =>
          PayService.payForBulkJobs({
            requestBody: {
              ids: jobs.map((job) => job.id),
            },
          }),
        {
          onError: (message: string) => {
            payErrorMessageRef.current = message;
          },
        },
      );
    },
    onMutate: () => setError(null),
    onSuccess: () => {
      setStep(BulkJobStep.JobsSubmittedSuccessfully);
      void queryClient.invalidateQueries({ queryKey: jobsKeys.lists() });
    },
    onError: async () => {
      const message = payErrorMessageRef.current;
      if (!message) {
        return;
      }
      setError(message);
      toast.error(message);
      if (message.toLowerCase().includes('insufficient balance')) {
        await refreshBalance();
        return;
      }
      setStep(BulkJobStep.JobsSubmittedWithErrors);
    },
  });

  const submitJobs = () => submitJobsMutation.mutate();

  const isLoading =
    isProcessingUploads || layoutsQuery.isLoading || updateJobsMutation.isPending || submitJobsMutation.isPending;

  return (
    <ModalContext.Provider
      value={{
        step,
        setStep,
        isLoading,
        billingChecksInProgress,
        onCancel,
        onSuccess,
        error: error ?? (billingAccountLoadFailed ? BILLING_ACCOUNT_LOAD_ERROR : null),
        layouts,
        createJobs,
        uploadFiles,
        jobs,
        totalDuration,
        submitJobs,
        isFormDisabled: isFormDisabled || !hasBillingAccount,
        balanceInCents,
        tierRate,
        refreshBalance,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useCurrentModalContext = () => useContext(ModalContext);
